import { Flex, Divider, Center } from "@chakra-ui/react";
import React from "react";
import { useLocation } from "react-router-dom";
import { useCurrentBreakpoint } from "../../../hooks/useCurrentBreakpoint";
import { SiAccounts } from "../../../images/icons/components/SiAccounts";
import { SiBalances } from "../../../images/icons/components/SiBalances";
import { SiDashboard } from "../../../images/icons/components/SiDashboard";
import { SiDataSources } from "../../../images/icons/components/SiDataSources";
import { SiReferAndEarn } from "../../../images/icons/components/SiReferAndEarn";
import { SiSettings } from "../../../images/icons/components/SiSettings";
import { SiTaxReports } from "../../../images/icons/components/SiTaxReports";
import { SiTransactions } from "../../../images/icons/components/SiTransactions";
import { SiUserProfile } from "../../../images/icons/components/SiUserProfile";
import {
  buildDashboardLink,
  buildDataSourcesLink,
  buildTransactionsLink,
  buildTaxReportsLink,
  buildAccountSettingsLink,
  buildAccountsLink,
  buildReferralsLink,
  buildUserSettingsLink,
  buildBalancesLink,
} from "../../../routers/routePaths";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { DrawerAccountNav } from "./DrawerAccountNav";
import { NavbarSupportButton } from "./NavbarSupportButton";
import { NavLink } from "./NavLink";
import { OverlayAccountNav } from "./OverlayAccountNav";

export const NavbarLinks = ({
  onClose,
  isCollapsed,
}: {
  onClose?: () => void;
  isCollapsed: boolean;
}) => {
  const { pathname } = useLocation();
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const dashboardLink = buildDashboardLink({ accountId });
  const dataSourcesLink = buildDataSourcesLink({ accountId });
  const transactionsLink = buildTransactionsLink({ accountId });
  const balancesLink = buildBalancesLink({ accountId });
  const taxReportsLink = buildTaxReportsLink({ accountId });
  const accountSettingsLink = buildAccountSettingsLink({ accountId });
  const accountsLink = buildAccountsLink({ accountId });
  const userSettingsLink = buildUserSettingsLink({ accountId });
  const referAndEarnLink = buildReferralsLink({ accountId });

  return (
    <Flex direction="column" gap={2} align="center" w="full">
      <AccountNav isCollapsed={isCollapsed} />
      <Flex direction="column" gap={2} align="center" w="full">
        <NavLink
          to={dashboardLink}
          icon={SiDashboard}
          isActive={pathname === dashboardLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Dashboard
        </NavLink>
        <NavLink
          to={dataSourcesLink}
          icon={SiDataSources}
          isActive={pathname === dataSourcesLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Data Sources
        </NavLink>
        <NavLink
          to={transactionsLink}
          icon={SiTransactions}
          isActive={pathname === transactionsLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Transactions
        </NavLink>
        <NavLink
          to={balancesLink}
          icon={SiBalances}
          isActive={pathname === balancesLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Balances
        </NavLink>
        <NavLink
          to={taxReportsLink}
          icon={SiTaxReports}
          isActive={pathname === taxReportsLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Tax Reports
        </NavLink>
        <NavLink
          to={accountSettingsLink}
          icon={SiSettings}
          isActive={pathname.startsWith(accountSettingsLink)}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Settings
        </NavLink>
        <Center py={2} w="full">
          <Divider w="75%" />
        </Center>
        <NavLink
          to={accountsLink}
          icon={SiAccounts}
          isActive={pathname === accountsLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Accounts
        </NavLink>
        <NavLink
          to={userSettingsLink}
          icon={SiUserProfile}
          isActive={pathname === userSettingsLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          User Profile
        </NavLink>
        <NavLink
          to={referAndEarnLink}
          icon={SiReferAndEarn}
          isActive={pathname === referAndEarnLink}
          onClick={onClose}
          isCollapsed={isCollapsed}
        >
          Refer & Earn
        </NavLink>
        <NavbarSupportButton isCollapsed={isCollapsed} />
      </Flex>
    </Flex>
  );
};

export function AccountNav({ isCollapsed }: { isCollapsed: boolean }) {
  const isMobile = useCurrentBreakpoint().isAtMost("sm");

  if (isMobile) {
    return <DrawerAccountNav />;
  }

  return <OverlayAccountNav isCollapsed={isCollapsed} />;
}
